import { useQuery } from "@tanstack/react-query";
import { isUiMockMode } from "@/config/features";
import { filterMockInvoices } from "@/fixtures/invoices";
import { searchInvoices } from "@/features/invoices/api/search";
import type { InvoiceSearchResponse } from "@/types/api";
import { keepPreviousPageData, paginatedSearchQueryOptions } from "@/lib/query/paginatedSearch";
import { invoiceKeys, type InvoiceSearchKeyParams } from "@/lib/query/keys";
import { isInvoiceAmountFilterValid } from "@/domain/invoices/invoiceFilterState";
import { isoDateToUnixEnd, isoDateToUnixStart } from "@/utils/format/isoDate";

/** Major currency units (e.g. "30.50") → minor units (cents) for API. */
function majorToMinorUnits(raw: string): number | undefined {
  const trimmed = raw.trim();
  if (!trimmed) return undefined;
  const n = Number(trimmed);
  if (!Number.isFinite(n)) return undefined;
  return Math.round(n * 100);
}

export function useInvoiceSearch({
  appliedStatuses,
  customerIds,
  pageSize,
  after,
  dateFrom,
  dateTo,
  amountMin,
  amountMax,
  dueDateFrom,
  dueDateTo,
  enabled = true,
}: {
  appliedStatuses: string[];
  customerIds: string[];
  pageSize: number;
  after?: string;
  dateFrom: string;
  dateTo: string;
  amountMin?: string;
  amountMax?: string;
  dueDateFrom?: string;
  dueDateTo?: string;
  enabled?: boolean;
}) {
  const customer_ids = customerIds.length > 0 ? customerIds : undefined;
  const statuses = appliedStatuses.length > 0 ? appliedStatuses : undefined;
  const amountOk = isInvoiceAmountFilterValid(amountMin ?? "", amountMax ?? "");

  const key: InvoiceSearchKeyParams = {
    statuses,
    customerIds: customer_ids,
    pageSize,
    after,
    createdFrom: isoDateToUnixStart(dateFrom),
    createdTo: isoDateToUnixEnd(dateTo),
    dueFrom: isoDateToUnixStart(dueDateFrom ?? ""),
    dueTo: isoDateToUnixEnd(dueDateTo ?? ""),
    amountDueMin: amountOk ? majorToMinorUnits(amountMin ?? "") : undefined,
    amountDueMax: amountOk ? majorToMinorUnits(amountMax ?? "") : undefined,
  };

  return useQuery({
    queryKey: invoiceKeys.search(key),
    enabled,
    queryFn: () => {
      if (isUiMockMode()) {
        return filterMockInvoices({
          statuses: appliedStatuses,
          customerIds,
          dateFrom,
          dateTo,
          amountMin: amountMin ?? "",
          amountMax: amountMax ?? "",
          dueDateFrom: dueDateFrom ?? "",
          dueDateTo: dueDateTo ?? "",
          pageSize,
          after,
        });
      }
      return searchInvoices({
        page: 0,
        page_size: pageSize,
        statuses,
        customer_ids,
        after_invoice_id: after,
        created_from: key.createdFrom,
        created_to: key.createdTo,
        due_from: key.dueFrom,
        due_to: key.dueTo,
        amount_due_min: key.amountDueMin,
        amount_due_max: key.amountDueMax,
      });
    },
    ...paginatedSearchQueryOptions,
    placeholderData: keepPreviousPageData<InvoiceSearchResponse>(key),
  });
}
